import { Box } from '@interstate/components/Box';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Typography } from '@interstate/components/Typography';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { createDealRootSelector } from '@makemydeal/dr-dash-components';
import * as offerRedux from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { mapAmdToVppVehicleConditionLabel } from '@makemydeal/dr-dash-store/utils/vehicleProtectionUtils';
import type { DealHistory } from '@makemydeal/dr-dash-types';
import { type AccelerateVehicleCondition } from '@makemydeal/dr-platform-types';
import { useSelector } from 'react-redux';
import { formatAprPercentage } from '../../../../utils/formatUtils';
import { MetricRowsContainer } from './AiAltDealStructureSlideout.style';
import { PrimaryBadge, PrimaryCardContainer } from './AltDealStructurePrimaryCard.style';
import { MetricCol } from './MetricCol';
import { MonthlyPaymentSection } from './MonthlyPaymentSection';

export const AltDealStructurePrimaryCard = () => {
    const theme = useInterstateTheme();
    const workingOffer = useSelector(offerSelectors.getWorkingOffer) || ({} as DealHistory);
    const useDealRootSelector = createDealRootSelector(workingOffer);
    const monthlyPayment = useDealRootSelector(offerSelectors.getMonthlyPayment);
    const sellingPrice = useDealRootSelector(offerSelectors.getSellingPrice);
    const downPayment = useDealRootSelector(offerSelectors.getDownPayment);
    const apr = useDealRootSelector(offerRedux.getSellRateOverride);
    const lenderName = useDealRootSelector(offerRedux.getCreditDecisionLenderName);
    const vehicle = useSelector(vehicleSelectors.getVehicle);

    const { condition, year, make, model, trim } = vehicle;
    const conditionLabel = mapAmdToVppVehicleConditionLabel(condition as AccelerateVehicleCondition);

    const vehicleTitle = [conditionLabel, year, make, model, trim].filter(Boolean).join(' ');

    const aprDisplay = formatAprPercentage(apr);
    const lenderDisplay = lenderName ?? '—';

    return (
        <PrimaryCardContainer theme={theme} data-testid="alt-deal-structure-primary-card">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                    <PrimaryBadge theme={theme} variant="info" data-testid="alt-deal-structure-primary">
                        Primary
                    </PrimaryBadge>
                    <Typography variant="h3" data-testid="alt-deal-structure-vehicle-title">
                        {vehicleTitle}
                    </Typography>
                </Box>
                <MonthlyPaymentSection monthlyPayment={monthlyPayment} />
            </Box>

            <MetricRowsContainer theme={theme} data-testid="alt-deal-structure-metrics-row">
                <MetricCol label="Down Payment" value={formatDollarsAndCents(downPayment)} />
                <MetricCol label="Selling Price" value={formatDollarsAndCents(sellingPrice)} />
                <MetricCol label="APR Rate" value={aprDisplay} />
                <MetricCol label="Lender" value={lenderDisplay} />
            </MetricRowsContainer>
        </PrimaryCardContainer>
    );
};
